import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { posts } from "./BlogPage";
import SeoHead from "../components/SeoHead";

// Full article content keyed by slug. In production this would come from a CMS.
const CONTENT = {
  "why-landlords-need-rent-management-software": [
    "If you're still tracking rent in a notebook or a WhatsApp group, you're not alone. Most landlords in Kenya manage their properties manually, and it works, until it doesn't.",
    "Late payments pile up. Tenants dispute amounts. You lose track of who paid what, when. And chasing people over the phone is exhausting.",
    "Rent management software like Paysafi changes this by giving you a single dashboard where you can see every property, every tenant, every payment, and every outstanding balance.",
    "Here's what you gain:\n\n- **Automatic tracking**: payments are logged as they happen.\n- **Overdue detection**: the system flags late tenants instantly.\n- **Reminders**: send SMS or AI calls with one click.\n- **History**: full audit trail for disputes or tax season.",
    "The best part? You can set it up in under 3 minutes. There's no reason not to try it.",
  ],
  "reduce-late-rent-payments": [
    "Late rent is the number one frustration for landlords. Here are five things you can do today to dramatically improve your collection rate.",
    "**1. Set clear due dates from day one.** Make sure tenants know the exact day rent is due before they sign. Paysafi lets you set a per tenant due day (e.g. 5th of every month).",
    "**2. Send reminders before the due date.** A friendly SMS 2-3 days before rent is due works wonders. Paysafi's bulk SMS feature can reach every tenant in seconds.",
    "**3. Follow up immediately when rent is overdue.** Don't wait a week. The moment the due date passes, send a follow up. Paysafi's AI voice agent can do this automatically.",
    "**4. Accept partial payments and track balances.** Some tenants can pay part now and the rest later. Rather than refusing, record the partial payment and track the balance. Paysafi handles this out of the box.",
    "**5. Keep records of every interaction.** When disputes arise, and they will, having a clear record of payments, SMS sent, and calls made protects you. Paysafi logs everything in a per tenant timeline.",
  ],
  "tenant-onboarding-best-practices": [
    "The first week of a tenancy sets the tone for everything that follows. Get it right, and you'll have fewer disputes and faster payments.",
    "**Collect a deposit and record it.** Always take a deposit before handing over keys. Record the amount in your system so there's no dispute later. Paysafi stores deposits on the tenant profile and logs them in the history.",
    "**Document the house condition.** Take photos and note any existing damage. This protects both you and the tenant when they eventually move out.",
    "**Set expectations about rent.** Make sure the tenant knows: the amount, the due day, accepted payment methods, and what happens when rent is late.",
    "**Add tenants to your system immediately.** Don't wait until the first rent is due. Add them to Paysafi on day one so you're tracking from the start.",
    "**Use the onboarding flow.** Paysafi walks you through creating a property and adding tenants step by step. It takes about 3 minutes.",
  ],
  "ai-calling-for-rent-collection": [
    "Nobody enjoys calling a tenant to ask about late rent. It's awkward, time consuming, and often unproductive.",
    "That's where AI voice agents come in. Paysafi's AI calling feature lets you trigger a phone call to a tenant, or a bulk batch of tenants, with one click.",
    "**How it works:** The AI agent calls the tenant, introduces itself, and reminds them about the outstanding rent. The tenant can respond naturally. The call is recorded, transcribed, and any commitments (like \"I'll pay by the 23rd\") are captured as notes.",
    "This means you get the follow up without the awkwardness. And because it's automated, you can reach dozens of tenants in the time it would take to call one.",
    "AI calling is available on Paysafi Pro and Enterprise plans.",
  ],
  "partial-payments-how-to-handle": [
    "When a tenant pays part of their rent, things get complicated fast. How much do they still owe? What about next month?",
    "Most landlords handle this with mental math or scribbled notes, which leads to disputes when numbers don't add up.",
    "**Paysafi's approach:** When you record a payment, you enter the actual amount paid. If it's less than the full rent, Paysafi marks the tenant as PARTIAL and shows the remaining balance. If they pay more than one month's rent, the excess automatically rolls over to cover future months.",
    "Every payment is logged with a date and can be voided if recorded in error. The tenant's status updates in real time: PAID, PARTIAL, PENDING, or OVERDUE.",
    "This means no more mental math, no more disputes, and no more lost money.",
  ],
  "property-management-kenya-guide": [
    "Managing rental property in Kenya comes with unique challenges, from legal requirements to cultural norms around rent payment.",
    "**Legal framework.** The Landlord and Tenant Act (Cap 301) governs rental relationships in Kenya. Key points: landlords must provide a rent receipt, give notice before eviction, and maintain the property in habitable condition.",
    "**Setting rent.** Research comparable properties in your area. Rent should cover your mortgage (if any), maintenance, and a reasonable return. Common rent ranges: KES 5,000-15,000 for bedsitters, KES 15,000-50,000 for 1-2 bedroom units in Nairobi.",
    "**Collecting rent.** M-Pesa is the dominant payment method. Many landlords accept bank transfers as well. Whichever method you use, always issue a receipt and record the payment in your system.",
    "**Maintenance and repairs.** Budget 5 to 10% of annual rent for maintenance. Respond to repair requests promptly; it builds goodwill and reduces tenant turnover.",
    "**Managing multiple properties.** As your portfolio grows, manual tracking becomes unsustainable. This is where property management software like Paysafi becomes essential: one dashboard for all your buildings, tenants, and payments.",
    "**Tenant communication.** Be professional, responsive, and documented. Use SMS for important notices. Paysafi's built in SMS and AI calling features help you communicate efficiently without the personal awkwardness.",
  ],
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = posts.find((p) => p.slug === slug);
  const paragraphs = CONTENT[slug];

  if (!post || !paragraphs) return <Navigate to="/blog" replace />;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    dateModified: post.date,
    author: { "@type": "Organization", name: "Paysafi" },
    publisher: {
      "@type": "Organization",
      name: "Paysafi",
      logo: {
        "@type": "ImageObject",
        url: "https://paysafi.com/logo.svg",
      },
    },
    image: "https://paysafi.com/og-image.png",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://paysafi.com/blog/${post.slug}`,
    },
  };

  return (
    <article>
      <SeoHead
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        type="article"
        jsonLd={articleJsonLd}
      />
      <section className="border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to blog
          </Link>
          <span className="mt-4 block text-xs font-medium text-indigo-600">
            {post.category}
          </span>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
            {post.title}
          </h1>
          <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
            <time>{post.date}</time>
            <span>·</span>
            <span>{post.readTime}</span>
          </div>
        </div>
      </section>
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="space-y-5">
            {paragraphs.map((p, i) => (
              <p
                key={i}
                className="text-base leading-relaxed text-gray-700 dark:text-gray-300 [&>strong]:font-semibold [&>strong]:text-gray-900 dark:[&>strong]:text-white"
                dangerouslySetInnerHTML={{
                  __html: p
                    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                    .replace(/\n/g, "<br/>")
                    .replace(/- \*\*/g, "<br/>- <strong>"),
                }}
              />
            ))}
          </div>
          <div className="mt-12 rounded-2xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-gray-800 p-6 text-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Ready to simplify your rent collection?
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Try Paysafi today.
            </p>
            <Link to="/signup" className="btn-primary mt-4 inline-flex px-6 py-2.5 text-sm">
              Get started
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
